
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { logger } from "@/utils/logger";

/**
 * Enum representing validation stages for agent files
 */
export enum ValidationStage {
  RECEIVED = "received",
  EXTENSION_SIZE_CHECK = "extension_size_check",
  VIRUS_SCAN = "virus_scan",
  STRUCTURE_VALIDATION = "structure_validation",
  QUARANTINE = "quarantine",
  APPROVED = "approved",
  REJECTED = "rejected"
}

/**
 * Type for validation result
 */
export interface ValidationResult {
  passed: boolean;
  stage: ValidationStage;
  message: string;
  details?: Record<string, any>;
}

/**
 * Type for storage upload result
 */
interface StorageUploadResult {
  path: string;
  id: string;
  fullPath: string;
}

/**
 * Service for validating agent files
 */
export const AgentValidationService = {
  /**
   * Maximum allowed file size in bytes (10MB)
   */
  MAX_FILE_SIZE: 10 * 1024 * 1024,

  /**
   * Valid file extensions
   */
  VALID_EXTENSIONS: ['js', 'ts', 'json', 'py', 'txt', 'md', 'zip'],

  /**
   * Validate file extension and size
   * @param file File to validate
   */
  validateExtensionAndSize(file: File): ValidationResult {
    try {
      logger.info("Validating file extension and size", { fileName: file.name, size: file.size });
      
      // Validate file extension
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !this.VALID_EXTENSIONS.includes(extension)) {
        logger.warn("Invalid file extension", { fileName: file.name, extension });
        return {
          passed: false,
          stage: ValidationStage.EXTENSION_SIZE_CHECK,
          message: `Invalid file extension: .${extension}. Allowed: ${this.VALID_EXTENSIONS.join(', ')}`
        };
      }
      
      // Validate file size
      if (file.size > this.MAX_FILE_SIZE) {
        logger.warn("File too large", { fileName: file.name, size: file.size });
        return {
          passed: false,
          stage: ValidationStage.EXTENSION_SIZE_CHECK,
          message: `File too large: ${(file.size / (1024 * 1024)).toFixed(2)}MB. Maximum allowed: ${this.MAX_FILE_SIZE / (1024 * 1024)}MB`
        };
      }
      
      logger.info("File extension and size validation passed", { fileName: file.name });
      return {
        passed: true,
        stage: ValidationStage.EXTENSION_SIZE_CHECK,
        message: "File extension and size validation passed"
      };
    } catch (error) {
      logger.error("Error validating file extension and size", { error, fileName: file.name });
      return {
        passed: false,
        stage: ValidationStage.EXTENSION_SIZE_CHECK,
        message: "Error validating file extension and size"
      };
    }
  },
  
  /**
   * Upload a file to quarantine storage for scanning
   * @param file File to upload to quarantine
   */
  async uploadToQuarantine(file: File): Promise<{filePath: string; uploadResult: StorageUploadResult | null}> {
    try {
      logger.info("Uploading file to quarantine", { fileName: file.name });
      
      const fileExt = file.name.split('.').pop();
      const safeName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `quarantine/${safeName}`;
      
      const { data, error } = await supabase
        .storage
        .from('agent_files')
        .upload(filePath, file);
        
      if (error) {
        logger.error("Error uploading file to quarantine", { error, fileName: file.name });
        return { filePath, uploadResult: null };
      }
      
      logger.info("File uploaded to quarantine successfully", { fileName: file.name, filePath });
      return { filePath, uploadResult: data };
    } catch (error) {
      logger.error("Exception uploading file to quarantine", { error, fileName: file.name });
      return { filePath: '', uploadResult: null };
    }
  },
  
  /**
   * Scan uploaded file for viruses and malware
   * Uses server-side scanning via edge function
   * @param filePath Path to the file in storage to scan
   */
  async scanForViruses(filePath: string): Promise<ValidationResult> {
    try {
      logger.info("Scanning file for viruses", { filePath });
      
      // Call edge function to perform scanning
      // Note: In a production environment, this would call an actual scanning service
      // For now we'll simulate the scan with a delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demo purposes, reject files with "virus" in the filename (case insensitive)
      if (filePath.toLowerCase().includes('virus')) {
        logger.warn("Virus detected in file", { filePath });
        return {
          passed: false,
          stage: ValidationStage.VIRUS_SCAN,
          message: "Malware detected in file",
          details: {
            detectionName: "DEMO-MALWARE-DETECTION",
            severity: "high"
          }
        };
      }
      
      logger.info("Virus scan passed", { filePath });
      return {
        passed: true,
        stage: ValidationStage.VIRUS_SCAN,
        message: "File passed virus scan"
      };
    } catch (error) {
      logger.error("Error during virus scanning", { error, filePath });
      return {
        passed: false,
        stage: ValidationStage.VIRUS_SCAN,
        message: "Error during virus scanning"
      };
    }
  },
  
  /**
   * Validate internal structure of files (especially for .zip and .py files)
   * @param filePath Path to the file to validate
   * @param fileType File extension/type
   */
  async validateStructure(filePath: string, fileType: string): Promise<ValidationResult> {
    try {
      logger.info("Validating file structure", { filePath, fileType });
      
      // Different validation based on file type
      switch (fileType.toLowerCase()) {
        case 'zip':
          // For zip files, we would extract and validate contents
          // Simulating validation with a delay
          await new Promise(resolve => setTimeout(resolve, 800));
          break;
          
        case 'py':
          // For Python files, check for potentially harmful imports or code patterns
          // Simulating validation with a delay
          await new Promise(resolve => setTimeout(resolve, 500));
          break;
          
        case 'js':
        case 'ts':
          // For JS/TS files, check for potentially harmful code patterns
          // Simulating validation with a delay
          await new Promise(resolve => setTimeout(resolve, 500));
          break;
          
        default:
          // Basic validation for other file types
          await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // For demo purposes, reject files with "invalid" in the filename (case insensitive)
      if (filePath.toLowerCase().includes('invalid')) {
        logger.warn("Invalid structure detected", { filePath, fileType });
        return {
          passed: false,
          stage: ValidationStage.STRUCTURE_VALIDATION,
          message: "Invalid file structure detected",
          details: {
            reason: "Demo rejection for files with 'invalid' in name"
          }
        };
      }
      
      logger.info("Structure validation passed", { filePath, fileType });
      return {
        passed: true,
        stage: ValidationStage.STRUCTURE_VALIDATION,
        message: "File structure validation passed"
      };
    } catch (error) {
      logger.error("Error validating file structure", { error, filePath, fileType });
      return {
        passed: false,
        stage: ValidationStage.STRUCTURE_VALIDATION,
        message: "Error validating file structure"
      };
    }
  },
  
  /**
   * Move file from quarantine to approved storage
   * @param filePath Path to the file in quarantine
   */
  async moveToApproved(filePath: string): Promise<string | null> {
    try {
      logger.info("Moving file from quarantine to approved storage", { filePath });
      
      // Extract the filename from the quarantine path
      const fileName = filePath.split('/').pop() || '';
      const approvedPath = `approved/${fileName}`;
      
      // Copy the file from quarantine to approved folder
      const { data: copyData, error: copyError } = await supabase
        .storage
        .from('agent_files')
        .copy(filePath, approvedPath);
        
      if (copyError) {
        logger.error("Error copying file to approved storage", { error: copyError, filePath });
        return null;
      }
      
      // Create a signed URL for the approved file
      const { data: urlData } = await supabase
        .storage
        .from('agent_files')
        .createSignedUrl(approvedPath, 157680000); // 5 years
        
      // Delete the quarantine file after successful copy
      await supabase
        .storage
        .from('agent_files')
        .remove([filePath]);
        
      logger.info("File moved to approved storage", { filePath, approvedPath });
      return urlData?.signedUrl || null;
    } catch (error) {
      logger.error("Exception moving file to approved storage", { error, filePath });
      return null;
    }
  },
  
  /**
   * Log validation event to database for audit purposes
   * @param validation Validation result to log
   * @param userId ID of the user who uploaded the file
   * @param filePath Path to the file
   */
  async logValidationEvent(validation: ValidationResult, userId: string, filePath: string): Promise<void> {
    try {
      logger.info("Logging validation event", { 
        validation: validation.stage, 
        passed: validation.passed, 
        userId, 
        filePath 
      });
      
      // Insert into custom table instead of directly using database table
      // This will be handled properly through a SQL migration
      // For now, just log to console
      logger.info("Validation event logged", {
        user_id: userId,
        file_path: filePath,
        validation_stage: validation.stage,
        passed: validation.passed,
        message: validation.message,
        details: validation.details || {}
      });
    } catch (error) {
      // Just log the error but don't throw, as logging failure shouldn't stop the process
      logger.error("Error logging validation event", { error, userId, filePath });
    }
  },
  
  /**
   * Process a file through the complete validation pipeline
   * @param file File to validate
   * @param userId ID of the user uploading the file
   */
  async processFile(file: File, userId: string): Promise<{ isValid: boolean; fileUrl: string | null; validationResults: ValidationResult[] }> {
    const results: ValidationResult[] = [];
    const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
    
    try {
      logger.info("Starting file validation process", { fileName: file.name, userId });
      
      // Initial reception log
      const receivedResult: ValidationResult = {
        passed: true,
        stage: ValidationStage.RECEIVED,
        message: "File received for validation"
      };
      results.push(receivedResult);
      await this.logValidationEvent(receivedResult, userId, file.name);
      
      // Step 1: Check file extension and size
      const extensionSizeResult = this.validateExtensionAndSize(file);
      results.push(extensionSizeResult);
      await this.logValidationEvent(extensionSizeResult, userId, file.name);
      
      if (!extensionSizeResult.passed) {
        toast({
          title: "File validation failed",
          description: extensionSizeResult.message,
          variant: "destructive",
        });
        return { isValid: false, fileUrl: null, validationResults: results };
      }
      
      // Step 2: Upload to quarantine
      const { filePath, uploadResult } = await this.uploadToQuarantine(file);
      if (!uploadResult) {
        const quarantineResult: ValidationResult = {
          passed: false,
          stage: ValidationStage.QUARANTINE,
          message: "Failed to upload file to quarantine"
        };
        results.push(quarantineResult);
        await this.logValidationEvent(quarantineResult, userId, file.name);
        
        toast({
          title: "Upload failed",
          description: "Failed to upload file for security scanning",
          variant: "destructive",
        });
        return { isValid: false, fileUrl: null, validationResults: results };
      }
      
      const quarantineResult: ValidationResult = {
        passed: true,
        stage: ValidationStage.QUARANTINE,
        message: "File uploaded to quarantine"
      };
      results.push(quarantineResult);
      await this.logValidationEvent(quarantineResult, userId, filePath);
      
      // Step 3: Scan for viruses
      const virusScanResult = await this.scanForViruses(filePath);
      results.push(virusScanResult);
      await this.logValidationEvent(virusScanResult, userId, filePath);
      
      if (!virusScanResult.passed) {
        toast({
          title: "Security scan failed",
          description: virusScanResult.message,
          variant: "destructive",
        });
        return { isValid: false, fileUrl: null, validationResults: results };
      }
      
      // Step 4: Validate file structure
      const structureResult = await this.validateStructure(filePath, fileExt);
      results.push(structureResult);
      await this.logValidationEvent(structureResult, userId, filePath);
      
      if (!structureResult.passed) {
        toast({
          title: "Structure validation failed",
          description: structureResult.message,
          variant: "destructive",
        });
        return { isValid: false, fileUrl: null, validationResults: results };
      }
      
      // Step 5: Move to approved storage
      const approvedFileUrl = await this.moveToApproved(filePath);
      
      if (!approvedFileUrl) {
        const approvedResult: ValidationResult = {
          passed: false,
          stage: ValidationStage.APPROVED,
          message: "Failed to move file to approved storage"
        };
        results.push(approvedResult);
        await this.logValidationEvent(approvedResult, userId, filePath);
        
        toast({
          title: "Processing failed",
          description: "Failed to finalize file processing",
          variant: "destructive",
        });
        return { isValid: false, fileUrl: null, validationResults: results };
      }
      
      // Final approval
      const approvedResult: ValidationResult = {
        passed: true,
        stage: ValidationStage.APPROVED,
        message: "File validated and approved"
      };
      results.push(approvedResult);
      await this.logValidationEvent(approvedResult, userId, approvedFileUrl);
      
      logger.info("File validation process completed successfully", { 
        fileName: file.name, 
        userId, 
        isValid: true 
      });
      
      return {
        isValid: true,
        fileUrl: approvedFileUrl,
        validationResults: results
      };
    } catch (error) {
      logger.error("Exception during file validation process", { error, fileName: file.name, userId });
      
      const errorResult: ValidationResult = {
        passed: false,
        stage: ValidationStage.REJECTED,
        message: `Validation process error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
      results.push(errorResult);
      
      try {
        await this.logValidationEvent(errorResult, userId, file.name);
      } catch (logError) {
        logger.error("Failed to log validation error", { logError });
      }
      
      toast({
        title: "Validation error",
        description: "An unexpected error occurred during file validation",
        variant: "destructive",
      });
      
      return { isValid: false, fileUrl: null, validationResults: results };
    }
  }
};
